import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Building2,
  Mail,
  Lock,
  Phone,
  User,
  DollarSign,
  MapPin,
  ArrowRight,
  CheckCircle,
  Home
} from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const US_STATES = [
  "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA",
  "HI", "ID", "IL", "IN", "IA", "KS", "KY", "LA", "ME", "MD",
  "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ",
  "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC",
  "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY"
];

const LOAN_TYPES = [
  { id: "fix_flip", label: "Fix & Flip" },
  { id: "bridge", label: "Bridge Loan" },
  { id: "rental", label: "Rental/DSCR" },
  { id: "construction", label: "Construction" },
  { id: "commercial", label: "Commercial" }
];

const PROPERTY_TYPES = [
  { id: "SFH", label: "Single Family" },
  { id: "MF", label: "Multi-Family" },
  { id: "Condo", label: "Condo" },
  { id: "Townhouse", label: "Townhouse" },
  { id: "Commercial", label: "Commercial" }
];

export default function LenderAuth() {
  const navigate = useNavigate();
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);
  
  // Login form
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  
  // Registration form
  const [registerForm, setRegisterForm] = useState({
    company_name: "",
    contact_name: "",
    email: "",
    password: "",
    phone: "",
    loan_types: [],
    min_loan_amount: 50000,
    max_loan_amount: 2000000,
    interest_rate_range: "8-15%",
    ltv_max: 75,
    arv_max: 70,
    states_covered: [],
    min_experience_deals: 0,
    property_types: []
  });

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/lender/login`,
        loginForm,
        { withCredentials: true }
      );
      toast.success("Welcome back!");
      navigate("/lender/portal");    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await axios.post(
        `${BACKEND_URL}/api/lender/register`,
        registerForm,
        { withCredentials: true }
      );
      toast.success("Registration successful!");
      navigate("/lender/portal");    } catch (err) {
      toast.error(err.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  const toggleLoanType = (type) => {
    setRegisterForm(prev => ({
      ...prev,
      loan_types: prev.loan_types.includes(type)
        ? prev.loan_types.filter(t => t !== type)
        : [...prev.loan_types, type]
    }));
  };

  const togglePropertyType = (type) => {
    setRegisterForm(prev => ({
      ...prev,
      property_types: prev.property_types.includes(type)
        ? prev.property_types.filter(t => t !== type)
        : [...prev.property_types, type]
    }));
  };

  const toggleState = (state) => {
    setRegisterForm(prev => ({
      ...prev,
      states_covered: prev.states_covered.includes(state)
        ? prev.states_covered.filter(s => s !== state)
        : [...prev.states_covered, state]
    }));
  };

  return (
    <div className="min-h-screen bg-black flex">
      {/* Left Side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-black to-[#0A0A0A] p-12 flex-col justify-between border-r border-white/[0.08]">
        <div>
          <div className="flex items-center gap-3 mb-12">
            <div className="w-12 h-12 bg-gold/10 backdrop-blur border border-gold/30 flex items-center justify-center">
              <Building2 className="w-7 h-7 text-gold" />
            </div>
            <span className="text-2xl font-bold text-white">DealForge</span>
          </div>
          
          <h1 className="text-4xl font-bold text-white mb-6">
            Lender Marketplace
          </h1>
          <p className="text-xl text-white/70 mb-8">
            Connect with verified real estate investors seeking funding for their deals.
          </p>
          
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-gold/20 flex items-center justify-center flex-shrink-0">
                <CheckCircle className="w-4 h-4 text-gold" />
              </div>
              <div>
                <h3 className="font-bold text-white">Quality Deal Flow</h3>
                <p className="text-sm text-white/70">
                  Access funding requests from active investors with analyzed deals.
                </p>
              </div>
            </div>
            
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-gold/20 flex items-center justify-center flex-shrink-0">
                <CheckCircle className="w-4 h-4 text-gold" />
              </div>
              <div>
                <h3 className="font-bold text-white">Criteria Matching</h3>
                <p className="text-sm text-white/70">
                  Set your lending parameters and only see deals that match.
                </p>
              </div>
            </div>
            
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-gold/20 flex items-center justify-center flex-shrink-0">
                <CheckCircle className="w-4 h-4 text-gold" />
              </div>
              <div>
                <h3 className="font-bold text-white">Direct Communication</h3>
                <p className="text-sm text-white/70">
                  Message investors directly through our secure platform.
                </p>
              </div>
            </div>
          </div>
        </div>
        
        <div className="text-sm text-white/70">
          Part of the DealForge ecosystem
        </div>
      </div>

      {/* Right Side - Forms */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Logo for mobile */}
          <div className="lg:hidden flex items-center gap-3 mb-8 justify-center">
            <div className="w-10 h-10 bg-gold/10 border border-gold/30 flex items-center justify-center">
              <Building2 className="w-6 h-6 text-gold" />
            </div>
            <span className="text-xl font-bold text-white">Lender Portal</span>
          </div>

          {/* Tab Switcher */}
          <div className="flex mb-8 bg-black rounded-2xl p-1 border border-white/[0.08]">
            <button
              onClick={() => { setIsLogin(true); setStep(1); }}
              className={`flex-1 py-2 text-sm font-medium rounded-lg transition-all duration-300 ${
                isLogin ? "bg-gold text-black" : "text-white/70 hover:text-white"
              }`}
            >
              Sign In
            </button>
            <button
              onClick={() => { setIsLogin(false); setStep(1); }}
              className={`flex-1 py-2 text-sm font-medium rounded-lg transition-all duration-300 ${
                !isLogin ? "bg-gold text-black" : "text-white/70 hover:text-white"
              }`}
            >
              Register
            </button>
          </div>

          {isLogin ? (
            /* Login Form */
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <Label className="text-white/70">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/70" />
                  <Input
                    type="email"
                    value={loginForm.email}
                    onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                    className="pl-10 bg-black border-white/[0.08] text-white"
                    placeholder="you@lender.com"
                    required
                  />
                </div>
              </div>
              
              <div>
                <Label className="text-white/70">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/70" />
                  <Input
                    type="password"
                    value={loginForm.password}
                    onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                    className="pl-10 bg-black border-white/[0.08] text-white"
                    placeholder="••••••••"
                    required
                  />
                </div>
              </div>
              
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-gold hover:bg-gold-light text-black font-bold"
              >
                {loading ? "Signing in..." : "Sign In"}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              
              <p className="text-center text-sm text-white/70">
                Looking to invest?{" "}
                <Link to="/login" className="text-gold hover:underline">
                  Investor Login
                </Link>
              </p>
            </form>
          ) : (
            /* Registration Form - Multi-step */
            <form onSubmit={handleRegister}>
              {step === 1 && (
                <div className="space-y-4">
                  <h2 className="text-lg font-bold text-white mb-4">Company Information</h2>
                  
                  <div>
                    <Label className="text-white/70">Company Name</Label>
                    <div className="relative">
                      <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/70" />
                      <Input
                        value={registerForm.company_name}
                        onChange={(e) => setRegisterForm({ ...registerForm, company_name: e.target.value })}
                        className="pl-10 bg-black border-white/[0.08] text-white"
                        placeholder="Your Lending Company"
                        required
                      />
                    </div>
                  </div>
                  
                  <div>
                    <Label className="text-white/70">Contact Name</Label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/70" />
                      <Input
                        value={registerForm.contact_name}
                        onChange={(e) => setRegisterForm({ ...registerForm, contact_name: e.target.value })}
                        className="pl-10 bg-black border-white/[0.08] text-white"
                        placeholder="John Smith"
                        required
                      />
                    </div>
                  </div>
                  
                  <div>
                    <Label className="text-white/70">Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/70" />
                      <Input
                        type="email"
                        value={registerForm.email}
                        onChange={(e) => setRegisterForm({ ...registerForm, email: e.target.value })}
                        className="pl-10 bg-black border-white/[0.08] text-white"
                        placeholder="you@lender.com"
                        required
                      />
                    </div>
                  </div>
                  
                  <div>
                    <Label className="text-white/70">Phone</Label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/70" />
                      <Input
                        value={registerForm.phone}
                        onChange={(e) => setRegisterForm({ ...registerForm, phone: e.target.value })}
                        className="pl-10 bg-black border-white/[0.08] text-white"
                        placeholder="(555) 123-4567"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <Label className="text-white/70">Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/70" />
                      <Input
                        type="password"
                        value={registerForm.password}
                        onChange={(e) => setRegisterForm({ ...registerForm, password: e.target.value })}
                        className="pl-10 bg-black border-white/[0.08] text-white"
                        placeholder="••••••••"
                        required
                      />
                    </div>
                  </div>
                  
                  <Button
                    type="button"
                    onClick={() => setStep(2)}
                    className="w-full bg-gold hover:bg-gold-light text-black font-bold"
                    disabled={!registerForm.company_name || !registerForm.email || !registerForm.password}
                  >
                    Next: Lending Criteria
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              )}

              {step === 2 && (
                <div className="space-y-4">
                  <h2 className="text-lg font-bold text-white mb-4">Lending Criteria</h2>
                  
                  <div>
                    <Label className="text-white/70">Loan Types</Label>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {LOAN_TYPES.map((type) => (
                        <button
                          key={type.id}
                          type="button"
                          onClick={() => toggleLoanType(type.id)}
                          className={`px-3 py-1.5 text-sm rounded-lg border transition-all duration-300 ${
                            registerForm.loan_types.includes(type.id)
                              ? "bg-gold border-gold text-black font-medium"
                              : "border-white/[0.1] text-white/70 hover:border-white/[0.15]"
                          }`}
                        >
                          {type.label}
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  <div>
                    <Label className="text-white/70">Property Types</Label>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {PROPERTY_TYPES.map((type) => (
                        <button
                          key={type.id}
                          type="button"
                          onClick={() => togglePropertyType(type.id)}
                          className={`px-3 py-1.5 text-sm rounded-lg border transition-all duration-300 ${
                            registerForm.property_types.includes(type.id)
                              ? "bg-gold border-gold text-black font-medium"
                              : "border-white/[0.1] text-white/70 hover:border-white/[0.15]"
                          }`}
                        >
                          {type.label}
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-white/70">Min Loan</Label>
                      <div className="relative">
                        <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/70" />
                        <Input
                          type="number"
                          value={registerForm.min_loan_amount}
                          onChange={(e) => setRegisterForm({ ...registerForm, min_loan_amount: parseInt(e.target.value) })}
                          className="pl-10 bg-black border-white/[0.08] text-white"
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="text-white/70">Max Loan</Label>
                      <div className="relative">
                        <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/70" />
                        <Input
                          type="number"
                          value={registerForm.max_loan_amount}
                          onChange={(e) => setRegisterForm({ ...registerForm, max_loan_amount: parseInt(e.target.value) })}
                          className="pl-10 bg-black border-white/[0.08] text-white"
                        />
                      </div>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-white/70">Max LTV %</Label>
                      <Input
                        type="number"
                        value={registerForm.ltv_max}
                        onChange={(e) => setRegisterForm({ ...registerForm, ltv_max: parseInt(e.target.value) })}
                        className="bg-black border-white/[0.08] text-white"
                      />
                    </div>
                    <div>
                      <Label className="text-white/70">Max ARV %</Label>
                      <Input
                        type="number"
                        value={registerForm.arv_max}
                        onChange={(e) => setRegisterForm({ ...registerForm, arv_max: parseInt(e.target.value) })}
                        className="bg-black border-white/[0.08] text-white"
                      />
                    </div>
                  </div>
                  
                  <div className="flex gap-3">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setStep(1)}
                      className="flex-1 border-white/[0.1] text-white/70"
                    >
                      Back
                    </Button>
                    <Button
                      type="button"
                      onClick={() => setStep(3)}
                      className="flex-1 bg-gold hover:bg-gold-light text-black font-bold"
                    >
                      Next
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </div>
                </div>
              )}

              {step === 3 && (
                <div className="space-y-4">
                  <h2 className="text-lg font-bold text-white mb-4">Geographic Coverage</h2>
                  
                  <div>
                    <Label className="text-white/70">
                      States Covered ({registerForm.states_covered.length} selected)
                    </Label>
                    <div className="flex flex-wrap gap-1.5 mt-2 max-h-48 overflow-y-auto p-2 bg-black rounded-2xl border border-white/[0.08]">
                      {US_STATES.map((state) => (
                        <button
                          key={state}
                          type="button"
                          onClick={() => toggleState(state)}
                          className={`px-2 py-1 text-xs rounded transition-all duration-300 ${
                            registerForm.states_covered.includes(state)
                              ? "bg-gold text-black font-medium"
                              : "bg-[#0A0A0A]/80 backdrop-blur-sm text-white/70 hover:bg-white/[0.06]"
                          }`}
                        >
                          {state}
                        </button>
                      ))}
                    </div>
                    <p className="text-xs text-white/70 mt-1">
                      Leave empty to cover all states
                    </p>
                  </div>
                  
                  <div>
                    <Label className="text-white/70">Interest Rate Range</Label>
                    <Input
                      value={registerForm.interest_rate_range}
                      onChange={(e) => setRegisterForm({ ...registerForm, interest_rate_range: e.target.value })}
                      className="bg-black border-white/[0.08] text-white"
                      placeholder="e.g., 8-15%"
                    />
                  </div>
                  
                  <div className="flex gap-3">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setStep(2)}
                      className="flex-1 border-white/[0.1] text-white/70"
                    >
                      Back
                    </Button>
                    <Button
                      type="submit"
                      disabled={loading}
                      className="flex-1 bg-gold hover:bg-gold-light text-black font-bold"
                    >
                      {loading ? "Creating..." : "Create Account"}
                    </Button>
                  </div>
                </div>
              )}
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
